<template>
	<div class="view-columns likeInput" :class="source ? 'available-columns':'visible-columns'" :style="dropStyle"
		 @dragover="dragOver" @dragenter="dragOver" @dragleave="dragLeave" @drop="drop" ref="container">
		<TransitionGroup :name="effect"><column-editor v-for="c in list" :key="c.value" :column="c"></column-editor></TransitionGroup>
		<div v-if="hovering && markerTop >= 0" class="drop-marker" :style="positionStyle"></div>
	</div>
</template>

<script setup lang="ts">
import {ref, computed, type CSSProperties, onMounted} from "vue";
import {ColumnListItem} from "@t/agenda/Columns.ts";
import ColumnEditor from "@fd/editors/view/ColumnEditor.vue";
import {calcVerticalPosition} from "@/utils/DragDropUtils.ts";

const props = defineProps<{
	source: boolean,
	list: ColumnListItem[]
}>();
const container = ref<HTMLDivElement>(null);
const hovering = ref<boolean>(false);
const markerTop = ref<number>(0);
const dropIndex = ref<number>(0);
const effect = ref<string>("none");
const emit = defineEmits(['coldrop']);

const positionStyle = computed<CSSProperties>(() => {
	if (hovering.value) {
		return { top: markerTop.value + "px" };
	}
});


function dragOver(e:DragEvent) {
	// is column dragged?
	const draggingColumn = e.dataTransfer.types.indexOf("column_id") >= 0;
	if (draggingColumn) {
		e.preventDefault();	// allow dropping
		hovering.value = true;
		if (props.source) {
			markerTop.value = -1;
			dropIndex.value = -1;
		} else {
			const pos = calcVerticalPosition(e, container.value, ":scope > .fd-view-column", { start:4, end: 4, gap: 4});
			markerTop.value = pos.offset;
			dropIndex.value = pos.index;
		}
		e.dataTransfer.dropEffect = "move";
	}
}

function dragLeave(e:DragEvent) {
	e.preventDefault();
	hovering.value = false;
	return false;
}

function drop(e:DragEvent) {
	e.preventDefault();
	const colId = e.dataTransfer.getData("column_id");
	console.log("Drop column = " + colId);
	hovering.value = false;
	emit('coldrop', colId, dropIndex.value);
	return false;
}

const dropStyle = computed<CSSProperties>(() => {
	if (hovering.value) {
		return { boxShadow: "inset 0 0 0 2px var(--selection-bg), var(--input-shadow)" };
	}
});

onMounted(() => {
	setTimeout(() => {
		effect.value = (props.source ? "slide-right" : "slide-left");
	}, 500);
});
</script>

<style>
.view-columns {
	min-height: 10em;
	min-width: 10em;
	overflow-y: auto;
	padding: 8px;
	display: flex;
	flex-direction: column;
	row-gap: 4px;
	position: relative;
}

.drop-marker {
	position: absolute;
	display: inline-block;
	top: 0;
	left: 0;
	width: 100%;
	height: 2px;
	background-color: var(--selection-bg);
	z-index: 10000;
	pointer-events: none;
	transition: all 250ms;
}

/* apply transition to moving elements */
.slide-right-move,
.slide-right-enter-active,
.slide-right-leave-active,
.slide-left-move,
.slide-left-enter-active,
.slide-left-leave-active
{
	transition: all 0.5s ease;
}

.slide-right-enter-from,
.slide-right-leave-to {
	opacity: 0;
	transform: scaleY(0.5) translateX(-4em);
}

.slide-left-enter-from,
.slide-left-leave-to {
	opacity: 0;
	transform: scaleY(0.5) translateX(4em);
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.slide-right-leave-active,
.slide-left-leave-active {
	/* position: relative; */
}

</style>